/**
 * WRO Allocations Panel
 *
 * Every ShipBob receiving order (WRO) this PO draws on, shown whole: each line
 * on the WRO, and which POs take how many of its units. Both directions are
 * covered — one container carrying several POs (245A + 245B in one WRO: each PO
 * takes its share of a line) and one PO arriving on several WROs (split
 * shipments, or a later WRO for units ShipBob found after a shortfall).
 *
 * The split is edited per WRO line from whichever PO is open: this PO's share,
 * the other POs' shares, and "+ PO" to hand part of a line to another PO. The
 * PO-number auto-match takes whatever the hand shares leave of a line, so a
 * container matched by name keeps feeding its PO after part of it is handed to
 * a second PO, and the shares of a line always add up to the line. Hand
 * allocations that count nowhere any more (their WRO was cancelled, or their PO
 * line deleted) are listed so they can be removed. Every change saves
 * immediately; the server refuses a split that does not fit.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import InventoryService from "services/InventoryService";
import ShippingMarkButton from "shared/components/ShippingMarkButton";
import { OrphanReason, OrphanedAllocation, POLineItem, WRO, WroAllocationsRequest, WroLineAllocations, WroLineAllocationsRequest, WroLineLink } from "models/InventoryModel";
import { shipbobWroUrl } from "shared/utils/shipbobLinks";

/** A PO that could take part of a WRO line: it must carry the item as a line */
export type PoOption = { poNumber: string; inventoryIds: string[] };

interface WroAllocationsPanelProps {
  poNumber: string;
  lineItems: POLineItem[];
  /** Server-computed links per line of the saved PO — which WROs this PO draws on */
  wroLinksByInventoryId: Record<string, WroLineLink[]>;
  /** Hand allocations per line of this PO that count nowhere any more */
  orphanedAllocationsByInventoryId?: Record<string, OrphanedAllocation[]>;
  wroOptions: WRO[];
  /** Other POs on the page, for handing part of a line to one of them */
  poOptions: PoOption[];
  /** false = the PO-number auto-match is off for this PO */
  autoMatch: boolean;
  selectedWroId: string | null;
  onSelectWro: (wroId: string | null) => void;
  /** The PO-level switch (auto-match on/off); allocations are passed through unchanged */
  onSaveAutoMatch: (request: WroAllocationsRequest) => Promise<void>;
  /** Replaces every allocation on one WRO, for every PO */
  onSaveWroLines: (wroId: string, lines: WroLineAllocationsRequest["lines"]) => Promise<void>;
  /** Lets the editor know what other POs take from the selected WRO (for Update WRO / Match) */
  onWroAllocationsLoaded?: (wroId: string, lines: WroLineAllocations[]) => void;
}

const ORPHAN_REASON: Record<OrphanReason, string> = {
  po_line_missing: "its PO line was deleted",
  wro_line_missing: "the WRO no longer carries the item",
  wro_cancelled: "the WRO was cancelled",
};

const LANDED = new Set(["Arrived", "Partially Arrived", "Arrived At Hub", "Partially Arrived At Hub", "Processing At Hub", "Processing", "Completed", "Cancelled"]);

export default function WroAllocationsPanel({
  poNumber,
  lineItems,
  wroLinksByInventoryId,
  orphanedAllocationsByInventoryId = {},
  wroOptions,
  poOptions,
  autoMatch,
  selectedWroId,
  onSelectWro,
  onSaveAutoMatch,
  onSaveWroLines,
  onWroAllocationsLoaded,
}: WroAllocationsPanelProps): JSX.Element {
  const service = useMemo(() => new InventoryService(), []);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Every line of each linked WRO with its allocations by PO, fetched per WRO
  const [matrixByWro, setMatrixByWro] = useState<Record<string, WroLineAllocations[]>>({});
  const [reloadKey, setReloadKey] = useState(0);
  // Share being typed, keyed "wroId|inventoryId|poNumber"
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // "+ PO" chooser open for one line, keyed "wroId|inventoryId"
  const [choosingFor, setChoosingFor] = useState<string | null>(null);
  // A PO picked with "+ PO" whose share is still being typed (nothing saved yet), keyed the same
  const [pendingPo, setPendingPo] = useState<Record<string, string>>({});
  // WROs whose line-by-line breakdown is folded away (a one-line summary stays)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggleCollapsed = (wroId: string) => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(wroId)) next.delete(wroId); else next.add(wroId);
    return next;
  });
  // "Add a WRO" flow
  const [addingWro, setAddingWro] = useState<WRO | null>(null);
  const [proposed, setProposed] = useState<Array<{ inventoryId: string; name: string; poQty: number; wroLineQty: number; taken: number; quantity: string }>>([]);

  const nameOf = (inventoryId: string, fallback?: string | null) =>
    lineItems.find(li => String(li.inventoryId) === inventoryId)?.inventoryName || fallback || inventoryId;

  // WROs this PO draws on (auto-matched or allocated), with this PO's own view of each
  const linkedWroIds = useMemo(() => {
    const ids = new Set<string>();
    for (const links of Object.values(wroLinksByInventoryId)) for (const link of links) ids.add(link.wroId);
    return Array.from(ids).sort();
  }, [wroLinksByInventoryId]);
  const kindOf = (wroId: string): "auto" | "allocation" | "mixed" => {
    const kinds = new Set<string>();
    for (const links of Object.values(wroLinksByInventoryId)) for (const link of links) if (link.wroId === wroId) kinds.add(link.linkKind);
    return kinds.size > 1 ? "mixed" : kinds.has("allocation") ? "allocation" : "auto";
  };
  // Everything this PO has allocated by hand (any WRO), for the auto-match switch
  const thisPoAllocations = useMemo(() => {
    const out: WroAllocationsRequest["allocations"] = [];
    for (const [inventoryId, links] of Object.entries(wroLinksByInventoryId)) {
      for (const link of links) if (link.linkKind === "allocation") out.push({ inventoryId, wroId: link.wroId, quantity: link.allocatedQuantity });
    }
    return out;
  }, [wroLinksByInventoryId]);

  useEffect(() => {
    if (linkedWroIds.length === 0) { if (selectedWroId) onSelectWro(null); return; }
    if (!selectedWroId || !linkedWroIds.includes(selectedWroId)) onSelectWro(linkedWroIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedWroIds]);

  useEffect(() => {
    const state = { cancelled: false };
    linkedWroIds.forEach(wroId => {
      service.getWroAllocations(wroId)
        .then(result => { if (!state.cancelled) setMatrixByWro(prev => ({ ...prev, [wroId]: result.lines })); })
        .catch(err => setErrorMessage(err.message));
    });
    return () => { state.cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedWroIds, reloadKey]);
  useEffect(() => {
    if (selectedWroId && matrixByWro[selectedWroId]) onWroAllocationsLoaded?.(selectedWroId, matrixByWro[selectedWroId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWroId, matrixByWro]);

  const run = async (work: () => Promise<void>) => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await work();
      setDrafts({});
      setChoosingFor(null);
      setPendingPo({});
      setReloadKey(k => k + 1);
    } catch (err: any) {
      setErrorMessage(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // The whole matrix of one WRO as the server stores it, with one (line, PO) entry changed.
  const matrixWith = (
    wroId: string,
    change: { inventoryId: string; poNumber: string; quantity: number | null | "remove" },
  ): WroLineAllocationsRequest["lines"] => {
    const lines = matrixByWro[wroId] ?? [];
    return lines.map(line => {
      let allocations = line.allocations.map(a => ({ poNumber: a.poNumber, quantity: a.allocatedQuantity }));
      if (line.inventoryId === change.inventoryId) {
        allocations = allocations.filter(a => a.poNumber !== change.poNumber);
        if (change.quantity !== "remove") allocations.push({ poNumber: change.poNumber, quantity: change.quantity });
      }
      return { inventoryId: line.inventoryId, allocations };
    });
  };

  // A typed share becomes an allocation; equal to the whole line = null ("all"); 0 = removed.
  // `current` is undefined for a PO just added with "+ PO": blank or 0 there simply drops it.
  const commitShare = (wroId: string, line: WroLineAllocations, po: string, current: number | null | undefined) => {
    const key = `${wroId}|${line.inventoryId}|${po}`;
    const lineKey = `${wroId}|${line.inventoryId}`;
    const raw = drafts[key];
    const clear = () => {
      setDrafts(prev => { const next = { ...prev }; delete next[key]; return next; });
      if (current === undefined) setPendingPo(prev => { const next = { ...prev }; delete next[lineKey]; return next; });
    };
    if (raw == null) { if (current === undefined) clear(); return; }
    const value = parseInt(raw, 10);
    if (isNaN(value) || value < 0 || (value === 0 && current === undefined)) { clear(); return; }
    const quantity = value >= line.expectedQuantity ? null : value;
    if (value > 0 && quantity === current) { clear(); return; }
    run(() => onSaveWroLines(wroId, matrixWith(wroId, { inventoryId: line.inventoryId, poNumber: po, quantity: value === 0 ? "remove" : quantity })));
  };

  const takenOn = (line: WroLineAllocations, exceptPo?: string) =>
    line.allocations.filter(a => a.poNumber !== exceptPo).reduce((sum, a) => sum + (a.allocatedQuantity ?? line.expectedQuantity), 0);

  // "+ PO" on a line opens a share field for the chosen PO, prefilled with what no PO takes
  // yet (0 when the auto-matched PO takes the rest — the person decides how much to move).
  const addPoToLine = (wroId: string, line: WroLineAllocations, po: PoOption) => {
    const lineKey = `${wroId}|${line.inventoryId}`;
    const free = line.autoMatchedPoNumbers.length > 0 ? 0 : Math.max(0, line.expectedQuantity - takenOn(line));
    setPendingPo(prev => ({ ...prev, [lineKey]: po.poNumber }));
    setDrafts(prev => ({ ...prev, [`${lineKey}|${po.poNumber}`]: String(free) }));
    setChoosingFor(null);
  };

  // Drops this PO's orphaned allocations (the server refuses to re-save them anyway)
  const removeOrphans = () => run(() => onSaveAutoMatch({ autoMatch, allocations: thisPoAllocations }));
  const myOrphans = Object.entries(orphanedAllocationsByInventoryId).flatMap(([inventoryId, list]) => list.map(o => ({ inventoryId, ...o })));

  const removeThisPoFromWro = (wroId: string) => {
    const lines = (matrixByWro[wroId] ?? []).map(line => ({
      inventoryId: line.inventoryId,
      allocations: line.allocations.filter(a => a.poNumber !== poNumber).map(a => ({ poNumber: a.poNumber, quantity: a.allocatedQuantity })),
    }));
    run(() => onSaveWroLines(wroId, lines));
  };

  // "Add a WRO": propose this PO's share of every line it carries — the PO's quantity, capped
  // by what other POs have already taken.
  const startAddWro = async (wro: WRO | null) => {
    setAddingWro(wro);
    setProposed([]);
    if (!wro) return;
    try {
      const { lines } = await service.getWroAllocations(wro.wroId);
      setMatrixByWro(prev => ({ ...prev, [wro.wroId]: lines }));
      const rows = lineItems
        .filter(li => li.inventoryId && li.quantity > 0)
        .map(li => {
          const line = lines.find(l => l.inventoryId === String(li.inventoryId));
          if (!line) return null;
          const taken = takenOn(line, poNumber);
          const remaining = Math.max(0, line.expectedQuantity - taken);
          return {
            inventoryId: String(li.inventoryId), name: li.inventoryName || String(li.inventoryId), poQty: li.quantity,
            wroLineQty: line.expectedQuantity, taken, quantity: String(Math.min(li.quantity, remaining)),
          };
        })
        .filter((row): row is NonNullable<typeof row> => row != null);
      setProposed(rows);
    } catch (err: any) {
      setErrorMessage(err.message);
    }
  };
  const confirmAddWro = () => {
    if (!addingWro) return;
    const wroId = addingWro.wroId;
    const wanted = new Map(proposed.map(row => [row.inventoryId, parseInt(row.quantity, 10)]));
    const lines = (matrixByWro[wroId] ?? []).map(line => {
      const allocations = line.allocations.filter(a => a.poNumber !== poNumber).map(a => ({ poNumber: a.poNumber, quantity: a.allocatedQuantity }));
      const qty = wanted.get(line.inventoryId);
      if (qty != null && !isNaN(qty) && qty > 0) allocations.push({ poNumber, quantity: qty >= line.expectedQuantity ? null : qty });
      return { inventoryId: line.inventoryId, allocations };
    });
    run(async () => { await onSaveWroLines(wroId, lines); setAddingWro(null); setProposed([]); onSelectWro(wroId); });
  };

  const addable = wroOptions.filter(w => !linkedWroIds.includes(w.wroId) && w.status !== "Cancelled");
  const wroMeta = (wroId: string) => wroOptions.find(w => w.wroId === wroId);
  const wroEta = (wro: WRO | undefined) => wro?.expectedArrivalDate && !wro.expectedArrivalDate.startsWith("9999") ? wro.expectedArrivalDate : null;
  const thisPoHasItem = (inventoryId: string) => lineItems.some(li => String(li.inventoryId) === inventoryId && li.quantity > 0);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
        <Typography variant="subtitle2">ShipBob receiving orders (WROs)</Typography>
        <Tooltip title={autoMatch
          ? "WROs whose ShipBob PO number starts with this PO number count automatically. Turn off to use only the allocations below."
          : "Auto-match is off: only the allocations below count for this PO."}>
          <FormControlLabel
            sx={{ ml: 0 }}
            control={<Switch size="small" checked={autoMatch} disabled={isSaving}
              onChange={(e) => run(() => onSaveAutoMatch({ autoMatch: e.target.checked, allocations: thisPoAllocations }))} />}
            label={<Typography variant="body2">Auto-match by PO number</Typography>}
          />
        </Tooltip>
        <Autocomplete
          size="small"
          sx={{ width: 320 }}
          options={addable}
          value={addingWro}
          getOptionLabel={(w) => `WRO ${w.wroId}${w.purchaseOrderNumber ? ` · ${w.purchaseOrderNumber}` : ""} · ${w.status}`}
          isOptionEqualToValue={(a, b) => a.wroId === b.wroId}
          onChange={(_, value) => startAddWro(value)}
          renderInput={(params) => (
            <TextField {...params} label="Add a WRO" placeholder="Allocate units from another WRO…" />
          )}
        />
      </Box>

      {errorMessage && (
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 1 }}>{errorMessage}</Alert>
      )}

      {addingWro && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          action={
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button color="inherit" size="small" onClick={() => { setAddingWro(null); setProposed([]); }}>Cancel</Button>
              <Button color="inherit" size="small" variant="outlined" disabled={isSaving || proposed.every(r => !(parseInt(r.quantity, 10) > 0))} onClick={confirmAddWro}>
                Allocate
              </Button>
            </Box>
          }
        >
          <strong>WRO {addingWro.wroId}</strong> — how many units of each line belong to {poNumber}?
          {proposed.length === 0 ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>This WRO carries none of the items on this PO.</Typography>
          ) : (
            <Table size="small" sx={{ mt: 0.5, "& td, & th": { py: 0.25, border: 0 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">On PO</TableCell>
                  <TableCell align="right">On WRO</TableCell>
                  <TableCell align="right">Other POs</TableCell>
                  <TableCell align="right">This PO takes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proposed.map(row => (
                  <TableRow key={row.inventoryId}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">{row.poQty}</TableCell>
                    <TableCell align="right">{row.wroLineQty}</TableCell>
                    <TableCell align="right">{row.taken || "—"}</TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={row.quantity}
                        onChange={(e) => setProposed(prev => prev.map(r => r.inventoryId === row.inventoryId ? { ...r, quantity: e.target.value } : r))}
                        inputProps={{ min: 0, max: row.wroLineQty, style: { textAlign: "right" } }}
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Alert>
      )}

      {myOrphans.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 1 }}
          action={<Button color="inherit" size="small" disabled={isSaving} onClick={removeOrphans}>Remove</Button>}
        >
          {poNumber} has hand allocations that count nowhere any more:{" "}
          {myOrphans.map((o, i) => (
            <React.Fragment key={`${o.inventoryId}|${o.wroId}`}>
              {i > 0 && "; "}
              {nameOf(o.inventoryId)} · {o.allocatedQuantity ?? "all"} units of{" "}
              <MuiLink href={shipbobWroUrl(o.wroId)} target="_blank" rel="noopener">WRO {o.wroId}</MuiLink> ({ORPHAN_REASON[o.reason]})
            </React.Fragment>
          ))}
          . They never block the PO-number match; remove them to tidy up.
        </Alert>
      )}

      {linkedWroIds.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {autoMatch
            ? "No ShipBob WRO matches this PO number yet. Add one above, link one by number, or create one."
            : "Auto-match is off and nothing is allocated: no WRO counts for this PO."}
        </Typography>
      ) : (
        <Table size="small" sx={{ "& td, & th": { py: 0.5 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 24 }} />
              <TableCell>WRO · item</TableCell>
              <TableCell align="right">On WRO line</TableCell>
              <TableCell align="right">Stowed</TableCell>
              <TableCell>Who takes it</TableCell>
              <TableCell align="right">Unallocated</TableCell>
              <TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {linkedWroIds.map(wroId => {
              const wro = wroMeta(wroId);
              const kind = kindOf(wroId);
              const lines = matrixByWro[wroId];
              const status = wro?.status ?? Object.values(wroLinksByInventoryId).flat().find(l => l.wroId === wroId)?.status ?? null;
              const landed = LANDED.has(status ?? "");
              const isCollapsed = collapsed.has(wroId);
              // One-line summary for the folded view: this PO's units, lines it draws on, and any unallocated remainder
              const mine = (lines ?? []).flatMap(line => line.allocations.filter(a => a.poNumber === poNumber).map(a => a.allocatedQuantity ?? line.expectedQuantity));
              const myAutoLines = (lines ?? []).filter(line => line.autoMatchedPoNumbers.includes(poNumber));
              const myUnits = mine.reduce((sum, n) => sum + n, 0) + myAutoLines.reduce((sum, line) => sum + line.autoQuantity, 0);
              const myLines = mine.length + myAutoLines.length;
              // Units of the WRO no PO takes at all (no hand share, and no PO matched by name to take the rest)
              const leftover = (lines ?? []).reduce((sum, line) => sum + (line.autoMatchedPoNumbers.length > 0 ? 0 : Math.max(0, line.expectedQuantity - takenOn(line))), 0);
              return (
                <React.Fragment key={wroId}>
                  <TableRow
                    sx={{ bgcolor: selectedWroId === wroId ? "action.selected" : "action.hover", cursor: linkedWroIds.length > 1 ? "pointer" : "default" }}
                    onClick={() => onSelectWro(wroId)}
                  >
                    <TableCell padding="checkbox">
                      {linkedWroIds.length > 1 && (
                        <input type="radio" checked={selectedWroId === wroId} onChange={() => onSelectWro(wroId)} aria-label={`Select WRO ${wroId}`} />
                      )}
                    </TableCell>
                    <TableCell colSpan={5}>
                      <Tooltip title={isCollapsed ? "Show the line-by-line breakdown" : "Hide the line-by-line breakdown"}>
                        <IconButton size="small" sx={{ mr: 0.5 }} onClick={(e) => { e.stopPropagation(); toggleCollapsed(wroId); }}>
                          {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <MuiLink href={shipbobWroUrl(wroId)} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>WRO {wroId}</MuiLink>
                      {` · ${status ?? "?"}`}
                      {wro?.fulfillmentCenterName ? ` · ${wro.fulfillmentCenterName}` : ""}
                      {wroEta(wro) ? ` · ETA ${wroEta(wro)}` : ""}
                      {wro?.purchaseOrderNumber ? ` · ShipBob PO "${wro.purchaseOrderNumber}"` : ""}
                      <Chip size="small" variant="outlined" sx={{ ml: 1 }}
                        label={kind === "auto" ? "matched by PO number" : kind === "allocation" ? "allocated by hand" : "partly allocated"} />
                      <Box component="span" sx={{ ml: 1 }}>
                        <ShippingMarkButton wroId={wroId} />
                      </Box>
                      {isCollapsed && lines && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1.5 }}>
                          {poNumber} takes {myUnits.toLocaleString()} units on {myLines} line{myLines === 1 ? "" : "s"}
                          {leftover > 0 ? ` · ${leftover.toLocaleString()} unallocated` : ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {kind !== "auto" && (
                        <Tooltip title={`Remove ${poNumber}'s allocations from WRO ${wroId} (other POs' shares stay)`}>
                          <IconButton size="small" disabled={isSaving} onClick={(e) => { e.stopPropagation(); removeThisPoFromWro(wroId); }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {isCollapsed ? null : !lines ? (
                    <TableRow><TableCell /><TableCell colSpan={6} sx={{ color: "text.secondary" }}>Loading lines…</TableCell></TableRow>
                  ) : lines.map(line => {
                    const lineKey = `${wroId}|${line.inventoryId}`;
                    const onThisPo = thisPoHasItem(line.inventoryId);
                    const taken = takenOn(line);
                    const unallocated = line.autoMatchedPoNumbers.length > 0 ? null : Math.max(0, line.expectedQuantity - taken);
                    const candidates = poOptions.filter(po => !line.allocations.some(a => a.poNumber === po.poNumber));
                    const pending = pendingPo[lineKey];
                    return (
                      <TableRow key={lineKey} sx={onThisPo ? undefined : { "& td": { color: "text.secondary" } }}>
                        <TableCell />
                        <TableCell sx={{ pl: 3 }}>
                          {nameOf(line.inventoryId, line.sku)}
                          {!onThisPo && <Typography variant="caption" sx={{ display: "block" }}>not on {poNumber}</Typography>}
                        </TableCell>
                        <TableCell align="right">{line.expectedQuantity}</TableCell>
                        <TableCell align="right">{line.stowedQuantity}</TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            {line.allocations.map(a => {
                              const key = `${lineKey}|${a.poNumber}`;
                              const mine = a.poNumber === poNumber;
                              return (
                                <Tooltip key={a.poNumber} title={landed
                                  ? "This WRO has landed; its split can no longer change"
                                  : `Units of this line that belong to ${a.poNumber} (0 removes it)`}>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label={a.poNumber}
                                    disabled={landed || isSaving}
                                    value={drafts[key] ?? String(a.allocatedQuantity ?? line.expectedQuantity)}
                                    onChange={(e) => setDrafts(prev => ({ ...prev, [key]: e.target.value }))}
                                    onBlur={() => commitShare(wroId, line, a.poNumber, a.allocatedQuantity)}
                                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                    inputProps={{ min: 0, max: line.expectedQuantity, style: { textAlign: "right" } }}
                                    sx={{ width: 120, ...(mine && { "& .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" } }) }}
                                  />
                                </Tooltip>
                              );
                            })}
                            {line.autoMatchedPoNumbers.map(po => (
                              <Tooltip key={po} title={line.allocations.length === 0
                                ? "Takes the whole line by PO-number match. Add a PO to split it."
                                : `Takes what the shares leave of the line (${line.autoQuantity}) by PO-number match`}>
                                <Chip size="small" variant="outlined" label={line.allocations.length === 0 ? `${po}: all (auto)` : `${po}: ${line.autoQuantity} (rest, auto)`} />
                              </Tooltip>
                            ))}
                            {pending && (
                              <Tooltip title={`Units of this line to give ${pending} (blank or 0 cancels)`}>
                                <TextField
                                  size="small"
                                  type="number"
                                  label={pending}
                                  autoFocus
                                  disabled={isSaving}
                                  value={drafts[`${lineKey}|${pending}`] ?? ""}
                                  onChange={(e) => setDrafts(prev => ({ ...prev, [`${lineKey}|${pending}`]: e.target.value }))}
                                  onBlur={() => commitShare(wroId, line, pending, undefined)}
                                  onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                                  inputProps={{ min: 0, max: line.expectedQuantity, style: { textAlign: "right" } }}
                                  sx={{ width: 120, "& .MuiOutlinedInput-notchedOutline": { borderStyle: "dashed" } }}
                                />
                              </Tooltip>
                            )}
                            {line.orphanedAllocations.map(o => (
                              <Tooltip key={`orphan-${o.poNumber}`} title={`Counts nowhere: ${ORPHAN_REASON[o.reason]}. Any save of this WRO's split drops it.`}>
                                <Chip size="small" color="warning" variant="outlined" label={`${o.poNumber}: ${o.allocatedQuantity ?? "all"} (orphaned)`} />
                              </Tooltip>
                            ))}
                            {!landed && !pending && (choosingFor === lineKey ? (
                              <Autocomplete
                                size="small"
                                sx={{ width: 220 }}
                                options={candidates}
                                getOptionLabel={(po) => po.poNumber}
                                getOptionDisabled={(po) => !po.inventoryIds.includes(line.inventoryId)}
                                renderOption={(props, po) => (
                                  <li {...props}>
                                    {po.poNumber}
                                    {!po.inventoryIds.includes(line.inventoryId) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>no line for this item</Typography>
                                    )}
                                  </li>
                                )}
                                onChange={(_, po) => { if (po) addPoToLine(wroId, line, po); }}
                                onClose={() => setChoosingFor(null)}
                                openOnFocus
                                autoFocus
                                renderInput={(params) => <TextField {...params} autoFocus placeholder="PO number…" />}
                              />
                            ) : (
                              <Tooltip title="Give part of this line to another PO (you type how many units)">
                                <Button size="small" startIcon={<AddIcon />} disabled={isSaving} onClick={() => setChoosingFor(lineKey)}>PO</Button>
                              </Tooltip>
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{unallocated != null && unallocated > 0 ? unallocated : ""}</TableCell>
                        <TableCell />
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
